import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib';
import type { Word } from '@shared/types';
import { ChevronLeftIcon, ChevronRightIcon } from '@shared/ui';
import { checkedTranslations } from '../SessionSenses/SessionSenses';
import './WordBrowser.css';

export type WordDotTone = 'ready' | 'manual' | 'danger' | 'none';

export interface WordRowMeta {
  /** Вторая строка строки; по умолчанию — основной перевод. */
  sub?: string;
  subTone?: 'danger';
  /** Точка статуса справа; 'none' — место под точку без цвета. */
  dot?: WordDotTone;
  /** Подпись точки для скринридера и подсказки. */
  dotLabel?: string;
}

export interface WordPreviewContext {
  /** Закрыть нижний лист (mobile), например после действия с карточкой. */
  closeSheet: () => void;
}

interface WordBrowserProps {
  words: Word[];
  /** ≤900px: карточка открывается нижним листом, а не в панели справа. */
  mobile: boolean;
  /** Заголовок и инструменты над списком (поиск, вкладки). */
  header?: ReactNode;
  /** Что показать вместо списка, когда слов нет. */
  empty: ReactNode;
  /** Карточка выбранного слова (обычно WordPreview). */
  renderPreview: (word: Word, ctx: WordPreviewContext) => ReactNode;
  rowMeta?: (word: Word) => WordRowMeta;
  /** Mobile-строка: перевод в одну строку со словом (публичный модуль) или под ним. */
  mobileRow?: 'inline' | 'stacked';
  /** Подсказка «← → листать карточки» под панелью (desktop). */
  keysHint?: boolean;
  getKey?: (word: Word, index: number) => string;
}

const defaultKey = (w: Word, i: number) => w.stateId ?? `${w.word}-${i}`;

/** Строка списка — «слово — перевод»: каждый выбранный перевод — своя карточка
 *  (2026-09-25), поэтому в строке переводы отмеченных смыслов. Контекст здесь не нужен. */
const rowTranslations = (w: Word) =>
  checkedTranslations(w.meanings?.[0]?.senses ?? [], w.sessionSenseIds ?? []);

interface SheetProps {
  title: string;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  children: ReactNode;
}

/** Нижний лист с карточкой (mobile): фокус внутри, Esc/фон закрывают, фокус возвращается к слову. */
function WordSheet({ title, onPrev, onNext, onClose, children }: SheetProps) {
  const { t } = useTranslation();
  const sheetRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    sheetRef.current?.querySelector<HTMLElement>('button')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !sheetRef.current) return;
      const items = Array.from(sheetRef.current.querySelectorAll<HTMLElement>('button:not([disabled])'));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      opener?.focus?.();
    };
  }, []);

  return createPortal(
    <>
      <div className="wb-sheet-backdrop" onClick={onClose} />
      <div ref={sheetRef} className="wb-sheet" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <span className="wb-sheet-grip" aria-hidden="true" />
        <div className="wb-sheet-head">
          <span id={titleId} className="wb-eyebrow">{title}</span>
          <button type="button" className="wb-sheet-close" aria-label={t('wordBrowser.close')} onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        {children}
        <div className="wb-sheet-nav">
          <button type="button" className="wb-sheet-btn" onClick={onPrev}>{t('wordBrowser.sheetPrev')}</button>
          <button type="button" className="wb-sheet-btn" onClick={onNext}>{t('wordBrowser.sheetNext')}</button>
        </div>
      </div>
    </>,
    document.body,
  );
}

/**
 * Слова модуля. Desktop: список (две колонки, если хватает места) и липкая
 * карточка выбранного слова справа, ←/→ листают. Mobile: список, тап открывает
 * карточку нижним листом с «Назад / Дальше». Выбор держится по ключу слова:
 * если слово пропало из списка (удалили, отфильтровали), выбор переходит на
 * соседнее — то, что встало на его место.
 */
export function WordBrowser({
  words,
  mobile,
  header,
  empty,
  renderPreview,
  rowMeta,
  mobileRow = 'stacked',
  keysHint = false,
  getKey = defaultKey,
}: WordBrowserProps) {
  const { t } = useTranslation();
  const [sel, setSel] = useState<{ key: string | null; index: number }>({ key: null, index: 0 });
  const [sheetOpen, setSheetOpen] = useState(false);
  const panelId = useId();
  const count = words.length;

  const found = sel.key === null ? -1 : words.findIndex((w, i) => getKey(w, i) === sel.key);
  const index = count === 0 ? -1 : found >= 0 ? found : Math.min(sel.index, count - 1);
  const current = index >= 0 ? words[index] : undefined;

  const select = useCallback(
    (i: number) => setSel({ key: getKey(words[i], i), index: i }),
    [getKey, words],
  );
  const go = useCallback(
    (delta: number) => {
      if (count === 0) return;
      select((Math.max(index, 0) + delta + count) % count);
    },
    [count, index, select],
  );
  const closeSheet = useCallback(() => setSheetOpen(false), []);
  const showSheet = mobile && sheetOpen && !!current;

  useEffect(() => {
    if (count === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return;
      // На мобайле листаем только открытый лист.
      if (mobile && !sheetOpen) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        go(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        go(-1);
      } else if (e.key === 'Escape' && sheetOpen) {
        setSheetOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [count, go, mobile, sheetOpen]);

  const counter = `${index + 1} / ${count}`;

  return (
    <div className="wb">
      <div className="wb-body">
        <div className="wb-words">
          {header}
          {count === 0 ? (
            empty
          ) : (
            <div className="wb-rows">
              {words.map((w, i) => {
                const meta = rowMeta?.(w) ?? {};
                const hasDot = meta.dot !== undefined;
                return (
                  <button
                    key={getKey(w, i)}
                    type="button"
                    className={cn('wb-row', hasDot && 'wb-row--dot', mobileRow === 'inline' && 'wb-row--inline')}
                    aria-current={!mobile && i === index ? 'true' : undefined}
                    aria-controls={mobile ? undefined : panelId}
                    onClick={() => {
                      select(i);
                      if (mobile) setSheetOpen(true);
                    }}
                  >
                    <span className="wb-row-idx">{String(i + 1).padStart(2, '0')}</span>
                    <span className="wb-row-text">
                      <span className="wb-row-en" lang="en">{w.word}</span>
                      <span className={cn('wb-row-ru', meta.subTone === 'danger' && 'wb-row-ru--danger')}>
                        {meta.sub ?? (rowTranslations(w) || w.ru)}
                      </span>
                    </span>
                    {hasDot && (
                      <span className="wb-dot" data-tone={meta.dot} title={meta.dotLabel}>
                        {meta.dotLabel && meta.dot !== 'none' && <span className="sr-only">{meta.dotLabel}</span>}
                      </span>
                    )}
                    <ChevronRightIcon className="wb-row-chev" aria-hidden="true" />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {!mobile && current && (
          <aside id={panelId} className="wb-panel" aria-label={t('wordBrowser.card')}>
            <div className="wb-panel-head">
              <span className="wb-eyebrow">{t('wordBrowser.card')}</span>
              <div className="wb-nav">
                <button type="button" className="wb-nav-btn" aria-label={t('wordBrowser.prev')} onClick={() => go(-1)}>
                  <ChevronLeftIcon aria-hidden="true" />
                </button>
                <span className="wb-counter" aria-live="polite">{counter}</span>
                <button type="button" className="wb-nav-btn" aria-label={t('wordBrowser.next')} onClick={() => go(1)}>
                  <ChevronRightIcon aria-hidden="true" />
                </button>
              </div>
            </div>
            {renderPreview(current, { closeSheet })}
            {keysHint && (
              <div className="wb-keys" aria-hidden="true">
                <span className="wb-key">←</span>
                <span className="wb-key">→</span>
                <span className="wb-keys-label">{t('wordBrowser.keysHint')}</span>
              </div>
            )}
          </aside>
        )}
      </div>

      {showSheet && (
        <WordSheet
          title={`${t('wordBrowser.card')} · ${counter}`}
          onPrev={() => go(-1)}
          onNext={() => go(1)}
          onClose={closeSheet}
        >
          {renderPreview(current, { closeSheet })}
        </WordSheet>
      )}
    </div>
  );
}
